import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// PDF GENERATION
// =============================================================================

let pdfMakeInstance = null;

async function getPdfMake() {
  if (pdfMakeInstance) return pdfMakeInstance;

  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');

  pdfMakeInstance = pdfMakeModule.default || pdfMakeModule;

  const pdfFonts = pdfFontsModule.default || pdfFontsModule;
  if (pdfFonts.pdfMake) {
    pdfMakeInstance.vfs = pdfFonts.pdfMake.vfs;
  } else if (pdfFonts.vfs) {
    pdfMakeInstance.vfs = pdfFonts.vfs;
  }

  return pdfMakeInstance;
}

const PDF_COLORS = {
  green: '#22C55E',
  blue: '#3B82F6',
  yellow: '#EAB308',
  orange: '#F97316',
  red: '#EF4444',
  headerBg: '#F3F4F6',
  borderColor: '#E5E7EB',
};

// =============================================================================
// CONFIG
// =============================================================================

const CATEGORIES = {
  A: {
    id: 'A',
    label: 'Category A',
    description: 'Interstates, 3+ lift',
    tiers: [
      { max: 45, pay: 102, color: 'green' },
      { max: 65, pay: 100, color: 'blue' },
      { max: 85, pay: 95, color: 'yellow' },
      { max: 149, pay: 80, color: 'orange' },
      { max: Infinity, pay: 50, color: 'red' },
    ],
    removalThreshold: 150,
  },
  B: {
    id: 'B',
    label: 'Category B',
    description: '2-lift over cold plane',
    tiers: [
      { max: 55, pay: 102, color: 'green' },
      { max: 75, pay: 100, color: 'blue' },
      { max: 95, pay: 95, color: 'yellow' },
      { max: 149, pay: 80, color: 'orange' },
      { max: Infinity, pay: 50, color: 'red' },
    ],
    removalThreshold: 150,
  },
  C: {
    id: 'C',
    label: 'Category C',
    description: 'Single-lift with prep',
    tiers: [
      { max: 55, pay: 102, color: 'green' },
      { max: 85, pay: 100, color: 'blue' },
      { max: 110, pay: 95, color: 'yellow' },
      { max: 149, pay: 80, color: 'orange' },
      { max: Infinity, pay: 50, color: 'red' },
    ],
    removalThreshold: 150,
  },
  D: {
    id: 'D',
    label: 'Category D',
    description: 'Improvement-based',
    disabled: true,
  },
};

const DEFAULT_SETTINGS = { category: 'A', unitPrice: 110, totalTons: 1000 };

// =============================================================================
// CALCULATION
// =============================================================================

const getTier = (iri, category) => {
  const tiers = CATEGORIES[category]?.tiers;
  if (!tiers) return { pay: 100, color: 'gray' };
  for (const t of tiers) if (iri < t.max) return t;
  return tiers[tiers.length - 1];
};

const calculate = (segments, settings) => {
  const { category, unitPrice, totalTons } = settings;
  const cat = CATEGORIES[category];
  if (!cat?.tiers || !segments?.length) return null;

  // Filter out exception/ignored segments
  const validSegments = segments.filter(seg => !seg.exception);
  if (!validSegments.length) return null;

  let totalLength = 0, iriSum = 0, count = 0, flagged = 0;

  // First pass: sort by station and calculate widths
  const sortedSegments = validSegments
    .map((seg, index) => {
      const startStation = seg.from_station_ft || 0;
      const stopStation = seg.to_station_ft || 0;
      const segmentWidth = seg.distance_ft || Math.abs(stopStation - startStation);
      return {
        ...seg,
        originalIndex: index,
        startStation,
        stopStation,
        segmentWidth,
      };
    })
    .sort((a, b) => a.startStation - b.startStation);

  // Second pass: calculate cumulative distances
  let cumulativeDistance = 0;
  const rows = sortedSegments.map((seg, i) => {
    const startDist = cumulativeDistance;
    const stopDist = cumulativeDistance + seg.segmentWidth;
    cumulativeDistance = stopDist;

    const mri = seg.iri_avg_in_per_mi || 0;
    const leftIRI = seg.iri_left_in_per_mi || seg.left_iri || 0;
    const rightIRI = seg.iri_right_in_per_mi || seg.right_iri || 0;
    const flag = mri >= cat.removalThreshold;
    const tier = getTier(mri, category);
    
    if (flag) flagged++;
    if (mri > 0) { iriSum += mri; count++; }
    totalLength += seg.segmentWidth;
    
    return {
      num: i + 1,
      mri,
      leftIRI,
      rightIRI,
      startDist,
      stopDist,
      startStation: seg.startStation,
      stopStation: seg.stopStation,
      len: seg.segmentWidth,
      flag,
      color: tier.color,
    };
  });

  const avgMRI = count > 0 ? iriSum / count : 0;
  const tier = getTier(avgMRI, category);
  const basePay = totalTons * unitPrice;
  const adjPay = basePay * (tier.pay / 100);

  return { rows, avgMRI, pay: tier.pay, color: tier.color, totalTons, basePay, adjPay, net: adjPay - basePay, flagged };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');
const fmtStation = (ft) => ft == null ? '—' : `${Math.floor(ft/100)}+${String(Math.round(ft%100)).padStart(2,'0')}`;
const fmtCurrency = (v) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// =============================================================================
// TIER BAR
// =============================================================================

const TierBar = ({ category, mri }) => {
  const tiers = CATEGORIES[category]?.tiers;
  if (!tiers) return null;
  
  const max = 180;
  const colors = { green: 'bg-green-500', blue: 'bg-blue-500', yellow: 'bg-yellow-500', orange: 'bg-orange-500', red: 'bg-red-500' };

  return (
    <div className="relative pt-5">
      {/* MRI Marker Label */}
      {mri > 0 && mri <= max && (
        <div 
          className="absolute top-0 -translate-x-1/2 text-xs font-bold bg-gray-800 text-white px-1.5 py-0.5 rounded-sm"
          style={{ left: `${(mri/max)*100}%` }}
        >
          {mri.toFixed(0)}
        </div>
      )}
      
      {/* Bar */}
      <div className="relative h-6 flex rounded-sm overflow-hidden border border-gray-300">
        {tiers.map((t, i) => {
          const prev = i === 0 ? 0 : tiers[i-1].max;
          const w = ((Math.min(t.max, max) - prev) / max) * 100;
          if (w <= 0) return null;
          return (
            <div key={i} className={cn('flex items-center justify-center text-xs font-bold text-white', colors[t.color])} style={{ width: `${w}%` }}>
              {w > 10 && `${t.pay}%`}
            </div>
          );
        })}
        
        {/* Marker Line */}
        {mri > 0 && mri <= max && (
          <div className="absolute top-0 bottom-0 w-0.5 bg-gray-800" style={{ left: `${(mri/max)*100}%` }} />
        )}
      </div>
      
      {/* Scale */}
      <div className="relative text-xs text-gray-400 mt-1 h-4">
        <span className="absolute left-0">0</span>
        {tiers.map((t, i) => {
          if (t.max >= max || t.max === Infinity) return null;
          const pos = (t.max / max) * 100;
          return (
            <span 
              key={i} 
              className="absolute -translate-x-1/2 text-gray-500 font-medium"
              style={{ left: `${pos}%` }}
            >
              {t.max}
            </span>
          );
        })}
        <span className="absolute right-0">{max}+</span>
      </div>
    </div>
  );
};

// =============================================================================
// PAY FACTOR MODAL
// =============================================================================

const PayFactorModal = ({ open, onClose, category }) => {
  const cat = CATEGORIES[category];
  const colors = { green: 'bg-green-100 text-green-700', blue: 'bg-blue-100 text-blue-700', yellow: 'bg-yellow-100 text-yellow-700', orange: 'bg-orange-100 text-orange-700', red: 'bg-red-100 text-red-700' };
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-sm shadow-xl max-w-lg w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <DialogTitle className="text-base font-semibold text-gray-900">
              LA DOTD Pay Factor Table
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3">Pay factor based on session average MRI</p>
            
            {/* Category Tabs */}
            <div className="flex gap-1 mb-4">
              {Object.values(CATEGORIES).filter(c => !c.disabled).map(c => (
                <div 
                  key={c.id}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-sm',
                    c.id === category ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                  )}
                >
                  {c.label}
                </div>
              ))}
            </div>

            <table className="w-full text-sm mb-4">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">MRI Range</th>
                  <th className="px-3 py-2 text-left">Pay Factor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cat?.tiers?.map((t, i) => {
                  const prev = i === 0 ? 0 : cat.tiers[i-1].max;
                  const rangeText = t.max === Infinity ? `≥ ${prev} in/mi` : `${prev} – ${t.max} in/mi`;
                  const statusText = t.pay >= 100 ? (t.pay > 100 ? 'Bonus' : 'Full Pay') : (t.pay >= 80 ? 'Penalty' : 'Remove/Replace');
                  return (
                    <tr key={i} className={t.color === 'red' ? 'bg-red-50' : ''}>
                      <td className="px-3 py-1.5 text-gray-700">{rangeText}</td>
                      <td className="px-3 py-1.5">
                        <span className={cn('inline-block px-2 py-0.5 rounded-sm text-xs font-bold', colors[t.color])}>
                          {t.pay}%
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-500 text-xs">{statusText}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="space-y-2 p-3 bg-gray-50 rounded-sm text-xs text-gray-600">
              <p><strong>Category A:</strong> Interstates, 3+ lift new construction</p>
              <p><strong>Category B:</strong> 2-lift overlay over cold plane</p>
              <p><strong>Category C:</strong> Single-lift with surface prep</p>
              <p><strong>Category D:</strong> Improvement-based (not implemented)</p>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <p><strong>Removal Threshold:</strong> ≥{cat?.removalThreshold} in/mi flagged for removal</p>
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// SUMMARY CARD
// =============================================================================

const SummaryCard = ({ label, value, unit, className = '', valueClassName = '' }) => (
  <div className={cn('bg-gray-50 border border-gray-200 rounded-sm px-3 py-2', className)}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={cn('text-xl font-bold text-gray-900', valueClassName)}>
      {value}
      {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
    </div>
  </div>
);

// =============================================================================
// MAIN
// =============================================================================

export default function LouisianaPayAdjustment({ project, session, segments }) {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...project?.payAdjustment });
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  const [colorRows, setColorRows] = useState(false);

  const data = useMemo(() => calculate(segments, settings), [segments, settings]);
  
  const rows = useMemo(() => {
    if (!data?.rows) return [];
    let r = [...data.rows];
    if (flaggedOnly) r = r.filter(x => x.flag);
    return r;
  }, [data?.rows, flaggedOnly]);

  const exportCSV = () => {
    const cat = CATEGORIES[settings.category];
    const sessionName = session?.name || session?.filename || 'Unnamed Session';
    const runDate = session?.run_date
      ? new Date(session.run_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '—';

    const header = [
      `Session:,${sessionName}`,
      `Route:,${project?.route_lrs_key || '—'}`,
      `Run Date:,${runDate}`,
      ``,
      `Category:,${cat?.label} - ${cat?.description}`,
      `Unit Price:,$${settings.unitPrice}/ton`,
      `Total Tons:,${settings.totalTons}`,
      ``,
      `Average MRI:,${data?.avgMRI?.toFixed(1) || '—'} in/mi`,
      `Pay Factor:,${data?.pay || '—'}%`,
      `Base Pay:,${data ? fmtCurrency(data.basePay) : '—'}`,
      `Adjusted Pay:,${data ? fmtCurrency(data.adjPay) : '—'}`,
      `Net Adjustment:,${data ? (data.net >= 0 ? '+' : '') + fmtCurrency(data.net) : '—'}`,
      `Flagged Segments:,${data?.flagged || 0} (>=${cat?.removalThreshold} in/mi)`,
      ``,
    ];

    const csv = [
      ...header,
      `Segment,Start Dist,Stop Dist,Start Station,Stop Station,Left IRI,Right IRI,MRI,Flagged`,
      ...rows.map(r => `${r.num},${r.startDist.toFixed(1)},${r.stopDist.toFixed(1)},${fmtStation(r.startStation)},${fmtStation(r.stopStation)},${r.leftIRI.toFixed(1)},${r.rightIRI.toFixed(1)},${r.mri.toFixed(1)},${r.flag ? 'Yes' : ''}`)
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'la-pay-adjustment.csv';
    a.click();
  };

  const exportPDF = async () => {
    const pdfMake = await getPdfMake();
    const cat = CATEGORIES[settings.category];
    const sessionName = session?.name || session?.filename || 'Unnamed Session';
    const runDate = session?.run_date
      ? new Date(session.run_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';

    // Build pay factor table rows
    const payFactorHeader = [
      { text: 'MRI Range', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Pay Factor', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Status', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
    ];
    const payFactorRows = cat?.tiers?.map((t, i) => {
      const prev = i === 0 ? 0 : cat.tiers[i - 1].max;
      const rangeText = t.max === Infinity ? `≥ ${prev} in/mi` : `${prev} – ${t.max} in/mi`;
      const statusText = t.pay >= 100 ? (t.pay > 100 ? 'Bonus' : 'Full Pay') : (t.pay >= 80 ? 'Penalty' : 'Remove/Replace');
      return [
        { text: rangeText, margin: [4, 3, 4, 3] },
        { text: `${t.pay}%`, bold: true, color: PDF_COLORS[t.color], margin: [4, 3, 4, 3] },
        { text: statusText, color: '#6B7280', margin: [4, 3, 4, 3] },
      ];
    }) || [];

    // Summary table rows
    const summaryRows = [
      [
        { text: 'Average MRI', bold: true, margin: [4, 2, 4, 2] },
        { text: `${data?.avgMRI?.toFixed(1) || '—'} in/mi`, alignment: 'right', margin: [4, 2, 4, 2] },
        { text: 'Pay Factor', bold: true, margin: [4, 2, 4, 2] },
        { text: `${data?.pay || '—'}%`, alignment: 'right', color: PDF_COLORS[data?.color] || '#000', bold: true, margin: [4, 2, 4, 2] },
      ],
      [
        { text: 'Base Pay', bold: true, margin: [4, 2, 4, 2] },
        { text: data ? fmtCurrency(data.basePay) : '—', alignment: 'right', margin: [4, 2, 4, 2] },
        { text: 'Adjusted Pay', bold: true, margin: [4, 2, 4, 2] },
        { text: data ? fmtCurrency(data.adjPay) : '—', alignment: 'right', margin: [4, 2, 4, 2] },
      ],
      [
        { text: 'Total Tons', bold: true, margin: [4, 2, 4, 2] },
        { text: `${settings.totalTons.toLocaleString()}`, alignment: 'right', margin: [4, 2, 4, 2] },
        { text: 'Net Adjustment', bold: true, margin: [4, 2, 4, 2] },
        {
          text: data ? `${data.net >= 0 ? '+' : ''}${fmtCurrency(data.net)}` : '—',
          alignment: 'right',
          color: data?.net > 0 ? PDF_COLORS.green : data?.net < 0 ? PDF_COLORS.red : '#000',
          bold: true,
          margin: [4, 2, 4, 2],
        },
      ],
    ];

    // Segments table
    const segmentHeaders = [
      { text: '#', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Start Dist', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Stop Dist', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Start Sta', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Stop Sta', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Left IRI', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'Right IRI', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
      { text: 'MRI', bold: true, fillColor: PDF_COLORS.headerBg, margin: [4, 4, 4, 4] },
    ];

    const segmentRows = rows.map(r => [
      { text: r.num, margin: [4, 2, 4, 2] },
      { text: r.startDist.toFixed(1), margin: [4, 2, 4, 2] },
      { text: r.stopDist.toFixed(1), margin: [4, 2, 4, 2] },
      { text: fmtStation(r.startStation), margin: [4, 2, 4, 2] },
      { text: fmtStation(r.stopStation), margin: [4, 2, 4, 2] },
      { text: r.leftIRI.toFixed(1), margin: [4, 2, 4, 2] },
      { text: r.rightIRI.toFixed(1), margin: [4, 2, 4, 2] },
      {
        text: r.mri.toFixed(1),
        bold: true,
        color: r.flag ? PDF_COLORS.red : '#000',
        fillColor: r.flag ? '#FEE2E2' : null,
        margin: [4, 2, 4, 2],
      },
    ]);

    const docDefinition = {
      pageSize: 'LETTER',
      pageMargins: [40, 40, 40, 40],
      content: [
        // Session Info
        {
          columns: [
            { width: '50%', text: [{ text: 'Session: ', bold: true }, sessionName] },
            { width: '50%', text: [{ text: 'Run Date: ', bold: true }, runDate], alignment: 'right' },
          ],
          margin: [0, 0, 0, 5],
        },
        {
          columns: [
            { width: '50%', text: [{ text: 'Route: ', bold: true }, project?.route_lrs_key || '—'] },
            { width: '50%', text: [{ text: 'Category: ', bold: true }, `${cat?.label} — ${cat?.description}`], alignment: 'right' },
          ],
          margin: [0, 0, 0, 5],
        },
        {
          columns: [
            { width: '50%', text: [{ text: 'Unit Price: ', bold: true }, `$${settings.unitPrice}/ton`] },
            { width: '50%', text: [{ text: 'Total Tons: ', bold: true }, settings.totalTons.toLocaleString()], alignment: 'right' },
          ],
          margin: [0, 0, 0, 15],
        },

        // Pay Factor Table
        { text: 'Pay Factor Table', style: 'sectionHeader', margin: [0, 0, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: [payFactorHeader, ...payFactorRows],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => PDF_COLORS.borderColor,
            vLineColor: () => PDF_COLORS.borderColor,
          },
          margin: [0, 0, 0, 15],
        },

        // Summary
        { text: 'Summary', style: 'sectionHeader', margin: [0, 0, 0, 5] },
        {
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: summaryRows,
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 10],
        },

        // Flagged warning
        ...(data?.flagged > 0 ? [{
          text: `⚠ ${data.flagged} segment${data.flagged > 1 ? 's' : ''} ≥${cat?.removalThreshold} in/mi — flagged for removal`,
          color: PDF_COLORS.red,
          bold: true,
          margin: [0, 0, 0, 15],
        }] : []),

        // Segments table
        { text: `Segments (${rows.length})`, style: 'sectionHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', '*', '*', '*', '*', '*', 'auto'],
            body: [segmentHeaders, ...segmentRows],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => PDF_COLORS.borderColor,
            vLineColor: () => PDF_COLORS.borderColor,
          },
          fontSize: 8,
        },
      ],
      styles: {
        sectionHeader: { fontSize: 11, bold: true, color: '#374151' },
      },
      defaultStyle: {
        fontSize: 10,
      },
    };

    pdfMake.createPdf(docDefinition).download('la-pay-adjustment.pdf');
  };

  const cat = CATEGORIES[settings.category];

  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      {/* Pay Factor Modal */}
      <PayFactorModal open={showPayTable} onClose={() => setShowPayTable(false)} category={settings.category} />

      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
        <h3 className="text-base font-semibold text-gray-900">LA DOTD Pay Adjustment</h3>
        
        <div className="flex-1" />

        {/* Pay Table Button */}
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Factors
        </button>

        <div className="h-5 w-px bg-gray-300" />

        {/* Category */}
        <Listbox value={settings.category} onChange={(v) => setSettings(s => ({ ...s, category: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm font-medium bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-red-500">
              {cat?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute right-0 z-30 mt-1 w-52 bg-white border border-gray-200 rounded-sm shadow-lg py-1 text-sm">
              {Object.values(CATEGORIES).map(c => (
                <ListboxOption key={c.id} value={c.id} disabled={c.disabled} className={({ active, disabled }) => cn('px-3 py-2 cursor-pointer', active && 'bg-red-50', disabled && 'opacity-40 cursor-not-allowed')}>
                  <span className="font-medium">{c.label}</span>
                  <span className="text-gray-500 ml-1">— {c.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <div className="h-5 w-px bg-gray-300" />

        {/* Simplified Settings: Unit Price and Total Tons */}
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-500">$/ton</label>
          <input
            type="number"
            value={settings.unitPrice}
            onChange={(e) => setSettings(s => ({ ...s, unitPrice: parseFloat(e.target.value) || 0 }))}
            className="h-8 w-16 px-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-red-500"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-500">Total Tons</label>
          <input
            type="number"
            value={settings.totalTons}
            onChange={(e) => setSettings(s => ({ ...s, totalTons: parseFloat(e.target.value) || 0 }))}
            className="h-8 w-20 px-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-red-500"
          />
        </div>

        <div className="h-5 w-px bg-gray-300" />

        <button onClick={exportCSV} className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" />
          CSV
        </button>
        <button onClick={exportPDF} className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5">
          <DocumentTextIcon className="h-4 w-4" />
          PDF
        </button>
      </div>

      {/* Summary */}
      {data ? (
        <div className="px-4 py-3 border-b border-gray-200 space-y-3">
          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-3">
            <SummaryCard label="Avg MRI" value={data.avgMRI.toFixed(1)} unit="in/mi" />
            <SummaryCard
              label="Pay Factor"
              value={`${data.pay}%`}
              valueClassName={cn(
                data.color === 'green' && 'text-green-600',
                data.color === 'blue' && 'text-blue-600',
                data.color === 'yellow' && 'text-yellow-600',
                data.color === 'orange' && 'text-orange-600',
                data.color === 'red' && 'text-red-600',
              )}
            />
            <SummaryCard label="Base Pay" value={fmtCurrency(data.basePay)} />
            <SummaryCard label="Adjusted Pay" value={fmtCurrency(data.adjPay)} />
            <SummaryCard
              label="Net Adjustment"
              value={`${data.net >= 0 ? '+' : ''}${fmtCurrency(data.net)}`}
              className={cn(
                data.net > 0 && 'bg-green-50 border-green-200',
                data.net < 0 && 'bg-red-50 border-red-200',
              )}
              valueClassName={cn(
                data.net > 0 && 'text-green-700',
                data.net < 0 && 'text-red-700',
              )}
            />
          </div>

          {/* Tier Bar */}
          <TierBar category={settings.category} mri={data.avgMRI} />

          {/* Flagged Alert */}
          {data.flagged > 0 && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {data.flagged} segment{data.flagged > 1 ? 's' : ''} ≥{cat.removalThreshold} in/mi — flagged for removal
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">No segment data</div>
      )}

      {/* Table */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-3 text-sm">
          <span className="text-gray-700 font-medium">Segments</span>
          <span className="text-gray-400">{data?.rows?.length || 0} total</span>
          {data?.flagged > 0 && (
            <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer ml-2">
              <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} className="w-3.5 h-3.5 text-red-600 rounded-sm" />
              Flagged only
            </label>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setColorRows(!colorRows)}
            className={cn(
              'h-7 px-3 text-xs font-medium rounded-sm border transition-colors',
              colorRows 
                ? 'bg-gray-800 text-white border-gray-800' 
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            )}
          >
            Color by IRI
          </button>
        </div>

        {rows.length > 0 ? (
          <div className="flex-1 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Start Dist</th>
                  <th className="px-4 py-2 text-left">Stop Dist</th>
                  <th className="px-4 py-2 text-left">Start Station</th>
                  <th className="px-4 py-2 text-left">Stop Station</th>
                  <th className="px-4 py-2 text-left">Left IRI</th>
                  <th className="px-4 py-2 text-left">Right IRI</th>
                  <th className="px-4 py-2 text-left">MRI</th>
                  <th className="px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const rowBg = colorRows ? {
                    green: 'bg-green-50',
                    blue: 'bg-blue-50',
                    yellow: 'bg-yellow-50',
                    orange: 'bg-orange-50',
                    red: 'bg-red-100',
                  }[r.color] || '' : '';
                  
                  return (
                    <tr key={r.num} className={cn('hover:bg-gray-50', r.flag && !colorRows && 'bg-red-50', rowBg)}>
                      <td className="px-4 py-2 font-medium text-gray-900">{r.num}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{r.startDist.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{r.stopDist.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{fmtStation(r.startStation)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{fmtStation(r.stopStation)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.leftIRI.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.rightIRI.toFixed(1)}</td>
                      <td className={cn('px-4 py-2 font-semibold', r.flag ? 'text-red-600' : 'text-gray-900')}>{r.mri.toFixed(1)}</td>
                      <td className="px-4 py-2">{r.flag && <ExclamationTriangleIcon className="h-4 w-4 text-red-500 inline" />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <XCircleIcon className="h-10 w-10 mx-auto mb-2" />
              <p>{flaggedOnly ? 'No flagged segments' : 'No segments'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}